import React, { useEffect, useContext, useMemo, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { useAppSelector, useAppDispatch } from '@/store';
import { fetchPlayerLimits, selectPlayerLimits, updatePlayerLimit } from '@/store/slices/playerLimits';

type LimitAlertProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    endpointUrl?: string;
    debounceMs?: number;
    type?: string;
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);
const DEFAULT_DEBOUNCE_MS = 500;

const LimitAlert = (componentProps: LimitAlertProps) => {
  let props = componentProps;

  const dataElementContext = useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(state => state.authentication.access_token);
  
  const playerLimitsData = useAppSelector(selectPlayerLimits);
  const playerLimitsState = useAppSelector(state => state.playerLimits);

  const limitType = props.properties?.type ?? '';
  const limitItem = playerLimitsData?.data?.[limitType as keyof typeof playerLimitsData.data];
  const storeValue = typeof limitItem === 'object' && limitItem !== null ? (limitItem as any).limit : undefined;

  const endpointUrl = props.properties?.endpointUrl || `${window.config.playerApiUrl}/player/data/player-limits`;
  const debounceMs = props.properties?.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const defaultValue = 0;
  const [isInitialized, setIsInitialized] = useState(false);
  const [inputValue, setInputValue] = useState<number>(defaultValue);
  const [debouncedValue, setDebouncedValue] = useState<number>(defaultValue);
  const lastProcessedValue = useRef<number | null>(null);

  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (accessToken && 
        !playerLimitsData && 
        !playerLimitsState?.loading && 
        !hasAttemptedFetch &&
        !fetchError &&
        !isFetchingRef.current) {
      
      setHasAttemptedFetch(true);
      isFetchingRef.current = true;
      
      dispatch(fetchPlayerLimits())
        .unwrap()
        .then((result) => {
          setFetchError(null);
          isFetchingRef.current = false;
        })
        .catch((error) => {
          setFetchError(error.message || 'Failed to fetch player limits');
          isFetchingRef.current = false;
        });
    }
  }, [accessToken, playerLimitsData, playerLimitsState?.loading, hasAttemptedFetch, fetchError, dispatch]);

  useEffect(() => {
    if (!accessToken) {
      setHasAttemptedFetch(false);
      setFetchError(null);
      isFetchingRef.current = false;
    }
  }, [accessToken]);

  useEffect(() => {
    if (playerLimitsData) {
      setHasAttemptedFetch(false);
      setFetchError(null);
      isFetchingRef.current = false;
    }
  }, [playerLimitsData]);

  const retryFetch = useCallback(() => {
    setHasAttemptedFetch(false);
    setFetchError(null);
    isFetchingRef.current = false;
  }, []);

  useEffect(() => {
    if (!isInitialized && playerLimitsData?.data && limitType && accessToken) {
      const limitExists = limitType in playerLimitsData.data;
      const limitValue = limitExists ? (playerLimitsData.data as any)[limitType]?.limit : undefined;

      if (limitExists && limitValue !== undefined) {
        setInputValue(limitValue);
        setDebouncedValue(limitValue);
        setIsInitialized(true);
      }
    }
  }, [playerLimitsData, limitType, isInitialized, accessToken]);

  const enhancedContext = useMemo(() => {
    const newContext = {
      ...dataElementContext,
      defaultValue: inputValue ?? 0,
      
      storeValue: storeValue,
      inputValue: inputValue,
      limitType: limitType,
      isInitialized: isInitialized,
      debouncedValue: debouncedValue,
      hasStoreData: !!playerLimitsData?.data,

      fetchError: fetchError,
      retryFetch: retryFetch,
      hasAttemptedFetch: hasAttemptedFetch,
      
      onSliderChange: (newValue: number | Event) => {
        if (typeof newValue !== 'number') {
          return;
        }
        
        const numValue = Number(newValue);
        if (isNaN(numValue)) {
          return;
        }
        
        setInputValue(numValue);
      }
    };
    
    return newContext;
  }, [
    dataElementContext, 
    storeValue, 
    inputValue,
    limitType, 
    playerLimitsData,
    isInitialized, 
    debouncedValue, 
    accessToken,
    fetchError,
    retryFetch,
    hasAttemptedFetch
  ]);

  useEffect(() => {
    if (storeValue !== undefined && storeValue !== inputValue && isInitialized) {
      setInputValue(storeValue);
    }
  }, [storeValue, isInitialized]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, debounceMs);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, debounceMs]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    
    if (!accessToken) {
      return;
    }
    
    if (debouncedValue === undefined || debouncedValue === null) {
      return;
    }

    const debouncedNum = Number(debouncedValue);
    const limitItem = playerLimitsData?.data?.[limitType as keyof typeof playerLimitsData.data];
    const currentLimitValue = (typeof limitItem === 'object' && limitItem !== null ? (limitItem as any).limit : limitItem) ?? 0;

    if (debouncedNum === currentLimitValue) {
      return;
    }
    
    if (debouncedNum === lastProcessedValue.current) {
      return;
    }
    
    const postData = {
      [limitType]: debouncedNum,
    };

    lastProcessedValue.current = debouncedNum;

    fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(postData),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        dispatch(updatePlayerLimit({
          limitType,
          limit: debouncedNum
        }));
        
        dispatch(fetchPlayerLimits())
          .unwrap()
          .then(() => {
          })
          .catch((error) => {
          });
      })
      .catch(error => {
        lastProcessedValue.current = currentLimitValue;
      });
  }, [debouncedValue, endpointUrl, limitType, accessToken, dispatch, playerLimitsData, isInitialized]);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={enhancedContext}>
        {props.children}
      </DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default LimitAlert;
